/**
 * PostTemplateEditDialog — SDialog-wrapped editor for a unified PostTemplate.
 *
 * Replaces the inline edit form previously used inside
 * `PerAuthorPostTemplatesLibrary`. Used for both create (empty draft) and
 * edit (existing template) modes.
 *
 * Covers ALL canonical PostTemplate fields:
 *   - Identity:    name, description, structure, funnelStage, tag
 *   - Routing:     goal, tone, lengthBucket
 *   - Scaffold:    scaffold + variables[] (key-only chips, comma-separated input)
 *   - Elements:    hook / thesis / proof / cta (4 inputs)
 *   - Policy:      hashtagPolicy, emojiPolicy (override cascade defaults)
 *   - Examples:    exampleEnglish
 *   - Provenance:  proofObjectHints (multi-select chips)
 *
 * Read-only `view` mode supported via `mode='view'` — same form, fields disabled.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SDialog } from '@modules/ui-components';
import type { PostTemplate } from '@modules/content-plan/types';
// Wave 2 — single source of truth for goal vocabulary + proof-object enum.
// Schema-models constants are also consumed by backend PromptInputResolver
// so frontend dropdowns and AI injection never drift.
import {
  DEFAULT_GOALS,
  PROOF_OBJECT_HINTS as PROOF_HINTS,
} from '@segmently/schema-models/content-plan/constants';

const DEFAULT_TONES = ['professional', 'personal', 'provocative', 'educational'] as const;
const FUNNEL_STAGES = ['TOFU', 'MOFU', 'BOFU'] as const;
const LENGTH_BUCKETS = ['short', 'medium', 'long'] as const;

type DraftTemplate = Partial<PostTemplate>;

type RecommendedFormatDraft = {
  formatType: string;
  weight?: number;
  reason?: string;
  bestForGoals?: string[];
  bestForAssetIntents?: string[];
};

export interface PostTemplateEditDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Initial template. When mode='create', pass `undefined` or a minimal draft.
   * When mode='edit', pass the full existing template.
   */
  template?: PostTemplate | DraftTemplate;
  mode: 'create' | 'edit' | 'view';
  /**
   * Save callback. Receives the full draft (excluding read-only/derived
   * fields like createdAt/updatedAt — caller fills those at persist time).
   */
  onSave?: (draft: DraftTemplate) => Promise<void> | void;
  /** Display title override; default derived from mode. */
  title?: string;
  /** Optional admin/system-catalogue field for editing the Firestore document id. */
  showTemplateId?: boolean;
  /** Allows changing the template id when `showTemplateId` is enabled. */
  allowTemplateIdEdit?: boolean;
}

const EMPTY_DRAFT: DraftTemplate = {
  name: '',
  description: '',
  structure: '',
  scaffold: '',
  goal: 'branding',
  tone: 'professional',
  lengthBucket: 'medium',
  funnelStage: 'MOFU',
  tag: '',
  hashtagPolicy: '',
  emojiPolicy: '',
  exampleEnglish: '',
  variables: [],
  elements: { hook: '', thesis: '', proof: '', cta: '' },
  proofObjectHints: [],
  compatibleFormatTypes: [],
  recommendedFormats: [],
};

export const PostTemplateEditDialog: React.FC<PostTemplateEditDialogProps> = ({
  open,
  onClose,
  template,
  mode,
  onSave,
  title,
  showTemplateId = false,
  allowTemplateIdEdit = false,
}) => {
  const [draft, setDraft] = useState<DraftTemplate>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readOnly = mode === 'view';

  useEffect(() => {
    if (open) {
      setDraft({ ...EMPTY_DRAFT, ...(template ?? {}) });
      setError(null);
    }
  }, [open, template]);

  const update = <K extends keyof DraftTemplate>(key: K, value: DraftTemplate[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };
  const updateElement = (key: 'hook' | 'thesis' | 'proof' | 'cta', value: string) => {
    setDraft((prev) => ({
      ...prev,
      elements: { ...(prev.elements ?? {}), [key]: value },
    }));
  };

  const variableKeys = useMemo(
    () => (draft.variables ?? []).map((v) => v.key).join(', '),
    [draft.variables],
  );
  const compatibleFormatTypesRaw = useMemo(
    () => ((draft as any).compatibleFormatTypes ?? []).join(', '),
    [draft],
  );
  const recommendedFormats = useMemo<RecommendedFormatDraft[]>(
    () => {
      const raw = (draft as any).recommendedFormats;
      return Array.isArray(raw) ? raw : [];
    },
    [draft],
  );

  const handleVariableKeysChange = (raw: string) => {
    const keys = raw
      .split(/[,\s]+/)
      .map((k) => k.trim())
      .filter(Boolean);
    update(
      'variables',
      keys.map((key) => ({
        key,
        label: key.replace(/_/g, ' '),
      })),
    );
  };

  const toggleProofHint = (hint: string) => {
    const current = draft.proofObjectHints ?? [];
    update(
      'proofObjectHints',
      current.includes(hint as any)
        ? (current.filter((h) => h !== hint) as any)
        : ([...current, hint] as any),
    );
  };

  const handleCompatibleFormatTypesChange = (raw: string) => {
    update(
      'compatibleFormatTypes' as keyof DraftTemplate,
      raw
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean) as any,
    );
  };

  const updateRecommendedFormat = (index: number, patch: Partial<RecommendedFormatDraft>) => {
    const next = recommendedFormats.map((item, currentIndex) => (
      currentIndex === index ? { ...item, ...patch } : item
    ));
    update('recommendedFormats' as keyof DraftTemplate, next as any);
  };

  const addRecommendedFormat = () => {
    update(
      'recommendedFormats' as keyof DraftTemplate,
      [
        ...recommendedFormats,
        { formatType: '', weight: 1, reason: '', bestForGoals: [], bestForAssetIntents: [] },
      ] as any,
    );
  };

  const removeRecommendedFormat = (index: number) => {
    update(
      'recommendedFormats' as keyof DraftTemplate,
      recommendedFormats.filter((_, currentIndex) => currentIndex !== index) as any,
    );
  };

  const parseCommaList = (raw: string) => raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const formatCommaList = (items?: string[]) => (items ?? []).join(', ');

  const handleRecommendedWeightChange = (index: number, raw: string) => {
    const parsed = Number(raw);
    updateRecommendedFormat(index, {
      weight: Number.isFinite(parsed) ? parsed : undefined,
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    if (showTemplateId && !draft.id?.trim()) {
      setError('Template ID is required.');
      return;
    }
    if (!draft.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle =
    title ??
    (mode === 'create' ? 'Create post template' : mode === 'view' ? 'View post template' : 'Edit post template');

  // SDialog convention (Storybook "Default" variant):
  //   X (top-left) is the SOLE dismiss affordance across all modes.
  //   Primary action (Save / Create) lives in the footer right.
  //   NO Cancel-labelled secondary action — Storybook explicitly forbids
  //   combining the default X with a footer Cancel (anti-pattern triggers
  //   a development-mode dev-warn).
  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth="md"
      fullWidth
      data-testid="post-template-edit-dialog"
      primaryAction={
        readOnly
          ? undefined
          : {
              label: mode === 'create' ? 'Create' : 'Save',
              onClick: handleSave,
              loading: saving,
              disabled: !draft.name?.trim(),
              'data-testid': 'post-template-save-btn',
            }
      }
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <Section label="Identity">
          <Stack spacing={1.5}>
            {showTemplateId && (
              <TextField
                label="Template ID"
                required
                size="small"
                fullWidth
                value={draft.id ?? ''}
                onChange={(e) => update('id', e.target.value)}
                disabled={readOnly || !allowTemplateIdEdit}
                helperText="Nested Firestore doc id. Lowercase letters, numbers, underscores, hyphens."
              />
            )}
            <TextField
              label="Name"
              required
              size="small"
              fullWidth
              value={draft.name ?? ''}
              onChange={(e) => update('name', e.target.value)}
              disabled={readOnly}
              autoFocus={!readOnly && mode === 'create'}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={1}
              value={draft.description ?? ''}
              onChange={(e) => update('description', e.target.value)}
              disabled={readOnly}
            />
            <TextField
              label="Structure"
              size="small"
              fullWidth
              placeholder="e.g. Hook → 3 bullets → CTA"
              value={draft.structure ?? ''}
              onChange={(e) => update('structure', e.target.value)}
              disabled={readOnly}
            />
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" fullWidth disabled={readOnly}>
                <InputLabel>Funnel stage</InputLabel>
                <Select
                  label="Funnel stage"
                  value={draft.funnelStage ?? ''}
                  onChange={(e) => update('funnelStage', e.target.value as any)}
                >
                  {FUNNEL_STAGES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Tag"
                size="small"
                fullWidth
                value={draft.tag ?? ''}
                onChange={(e) => update('tag', e.target.value)}
                disabled={readOnly}
              />
            </Stack>
          </Stack>
        </Section>

        <Divider />

        <Section label="Goal-driven routing">
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 160 }} disabled={readOnly}>
              <InputLabel>Goal</InputLabel>
              <Select
                label="Goal"
                value={draft.goal ?? ''}
                onChange={(e) => update('goal', e.target.value as any)}
              >
                {DEFAULT_GOALS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }} disabled={readOnly}>
              <InputLabel>Tone</InputLabel>
              <Select
                label="Tone"
                value={draft.tone ?? ''}
                onChange={(e) => update('tone', e.target.value as any)}
              >
                {DEFAULT_TONES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={readOnly}>
              <InputLabel>Length bucket</InputLabel>
              <Select
                label="Length bucket"
                value={draft.lengthBucket ?? ''}
                onChange={(e) => update('lengthBucket', e.target.value as any)}
              >
                {LENGTH_BUCKETS.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Section>

        <Divider />

        <Section label="Format compatibility">
          <Stack spacing={1.5}>
            <TextField
              label="Compatible format types"
              size="small"
              fullWidth
              placeholder="long_text, carousel, thread"
              value={compatibleFormatTypesRaw}
              onChange={(e) => handleCompatibleFormatTypesChange(e.target.value)}
              disabled={readOnly}
              helperText="Matches platform formatType, not platform-local format id."
            />
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Recommended formats
                </Typography>
                {!readOnly && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRecommendedFormat}>
                    Add format
                  </Button>
                )}
              </Stack>
              {recommendedFormats.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No recommended format mapping yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {recommendedFormats.map((item, index) => (
                    <Box
                      key={`${item.formatType || 'format'}-${index}`}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.25,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            label="Format type"
                            size="small"
                            fullWidth
                            value={item.formatType ?? ''}
                            onChange={(e) => updateRecommendedFormat(index, { formatType: e.target.value })}
                            disabled={readOnly}
                            helperText="Matches platform formatType."
                          />
                          <TextField
                            label="Weight"
                            size="small"
                            type="number"
                            sx={{ width: 120 }}
                            value={item.weight ?? ''}
                            onChange={(e) => handleRecommendedWeightChange(index, e.target.value)}
                            disabled={readOnly}
                            inputProps={{ step: 0.1, min: 0 }}
                          />
                          {!readOnly && (
                            <IconButton
                              size="small"
                              aria-label={`Remove recommended format ${item.formatType || index + 1}`}
                              onClick={() => removeRecommendedFormat(index)}
                              sx={{ mt: 0.5 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        <TextField
                          label="Reason"
                          size="small"
                          fullWidth
                          value={item.reason ?? ''}
                          onChange={(e) => updateRecommendedFormat(index, { reason: e.target.value })}
                          disabled={readOnly}
                        />
                        <Stack direction="row" spacing={1}>
                          <TextField
                            label="Best for goals"
                            size="small"
                            fullWidth
                            value={formatCommaList(item.bestForGoals)}
                            onChange={(e) => updateRecommendedFormat(index, { bestForGoals: parseCommaList(e.target.value) })}
                            disabled={readOnly}
                            helperText="Comma-separated goals."
                          />
                          <TextField
                            label="Best for asset intents"
                            size="small"
                            fullWidth
                            value={formatCommaList(item.bestForAssetIntents)}
                            onChange={(e) => updateRecommendedFormat(index, { bestForAssetIntents: parseCommaList(e.target.value) })}
                            disabled={readOnly}
                            helperText="Comma-separated asset intents."
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Section>

        <Divider />

        <Section label="Scaffold">
          <Stack spacing={1.5}>
            <TextField
              label="Scaffold body"
              multiline
              minRows={3}
              fullWidth
              size="small"
              placeholder="Use {variable} placeholders, e.g. Most {audience} think {belief}."
              value={draft.scaffold ?? ''}
              onChange={(e) => update('scaffold', e.target.value)}
              disabled={readOnly}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <TextField
              label="Variables (comma-separated keys)"
              size="small"
              fullWidth
              placeholder="audience, belief, proof, cta"
              value={variableKeys}
              onChange={(e) => handleVariableKeysChange(e.target.value)}
              disabled={readOnly}
              helperText="Keys must match {placeholders} in the scaffold."
            />
          </Stack>
        </Section>

        <Divider />

        <Section label="Elements (required beats)">
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Hook"
                size="small"
                fullWidth
                value={draft.elements?.hook ?? ''}
                onChange={(e) => updateElement('hook', e.target.value)}
                disabled={readOnly}
              />
              <TextField
                label="Thesis"
                size="small"
                fullWidth
                value={draft.elements?.thesis ?? ''}
                onChange={(e) => updateElement('thesis', e.target.value)}
                disabled={readOnly}
              />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Proof"
                size="small"
                fullWidth
                value={draft.elements?.proof ?? ''}
                onChange={(e) => updateElement('proof', e.target.value)}
                disabled={readOnly}
              />
              <TextField
                label="CTA"
                size="small"
                fullWidth
                value={draft.elements?.cta ?? ''}
                onChange={(e) => updateElement('cta', e.target.value)}
                disabled={readOnly}
              />
            </Stack>
          </Stack>
        </Section>

        <Divider />

        <Section label="Policy override (cascades from PlatformVoice)">
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Hashtag policy"
              size="small"
              fullWidth
              placeholder="LI 1-2; X 0"
              value={draft.hashtagPolicy ?? ''}
              onChange={(e) => update('hashtagPolicy', e.target.value)}
              disabled={readOnly}
              helperText="Empty → falls back to PlatformVoice."
            />
            <TextField
              label="Emoji policy"
              size="small"
              fullWidth
              placeholder="professional 0-1"
              value={draft.emojiPolicy ?? ''}
              onChange={(e) => update('emojiPolicy', e.target.value)}
              disabled={readOnly}
              helperText="Empty → falls back to PlatformVoice."
            />
          </Stack>
        </Section>

        <Divider />

        <Section label="Proof object hints">
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {PROOF_HINTS.map((h) => {
              const selected = (draft.proofObjectHints ?? []).includes(h as any);
              return (
                <Chip
                  key={h}
                  label={h}
                  size="small"
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={readOnly ? undefined : () => toggleProofHint(h)}
                  sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                />
              );
            })}
          </Stack>
        </Section>

        <Divider />

        <Section label="Example (English)">
          <TextField
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={draft.exampleEnglish ?? ''}
            onChange={(e) => update('exampleEnglish', e.target.value)}
            disabled={readOnly}
          />
        </Section>
      </Stack>
    </SDialog>
  );
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

export default PostTemplateEditDialog;
